import React from 'react';
import {IProxyServer, ProxyServer} from 'types/ProxyServer';
import FormikTextField from 'components/inputs/FormikTextField';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import {Offset} from 'components/Offset';
import Panel from 'components/Panel';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import {ProxyReferencesPanel} from 'features/layout/CommonPage/panels/ReferencesPanel';
import FormikPassphrase from 'components/inputs/FormikPassphrase';
import {castToSend} from 'common/yup/castToSend';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import api from 'api';

type ProxyFormProps = {
   initialValues?: IProxyServer;
   readonly?: boolean;
   onSubmit?: (values: IProxyServer, helpers: FormOnSubmitHelpers<IProxyServer>) => void;
   onClose?: () => void;
};

const ProxyForm = ({
   initialValues,
   readonly,
   onSubmit = () => {},
   onClose = () => {},
}: ProxyFormProps) => {
   const {t} = useTranslation();
   return (
      <Formik
         initialValues={ProxyServer.cast(initialValues) as IProxyServer}
         validationSchema={ProxyServer}
         onSubmit={(values: IProxyServer, helpers: FormOnSubmitHelpers<IProxyServer>) =>
            onSubmit(castToSend(ProxyServer, values), helpers)
         }
      >
         <>
            <Grid component={Panel} container spacing={2}>
               <Grid item xs={6}>
                  <FormikTextField
                     autoFocus
                     name="name"
                     placeholder={t('Name')}
                     disabled={readonly}
                  />
               </Grid>
               <Grid item xs={12}>
                  <FormikTextField
                     name="description"
                     placeholder={t('Description')}
                     multiline
                     disabled={readonly}
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikTextField name="proxyType" placeholder={t('Proxy Protocol')} disabled />
               </Grid>
               <Offset xs={6} />
               <Grid item xs={6}>
                  <FormikTextField name="address" placeholder={t('Address')} disabled={readonly} />
               </Grid>
               <Grid item xs={6}>
                  <FormikTextField name="port" placeholder={t('Port')} disabled={readonly} />
               </Grid>
               <Grid item xs={6}>
                  <FormikTextField
                     name="username"
                     placeholder={t('Username')}
                     disabled={readonly}
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikPassphrase
                     name="password"
                     placeholder={t('Password')}
                     disabled={readonly}
                     fetchUrl={
                        initialValues ? `${api.proxies}/${initialValues.id}/getpassword` : undefined
                     }
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikCheckbox
                     name="defaultProxy"
                     label={t('Default Proxy Server')}
                     disabled={readonly}
                  />
               </Grid>
               {initialValues && (
                  <Grid item xs={12}>
                     <LastUpdatedLabel
                        date={initialValues.timestamp}
                        author={initialValues.lastUser}
                     />
                  </Grid>
               )}
               {initialValues && readonly && <ProxyReferencesPanel id={initialValues.id} />}
            </Grid>
            {!readonly && <FormFooterButtons cancel={onClose} />}
         </>
      </Formik>
   );
};

export default ProxyForm;
